import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { filterUndefined, trackByRef } from 'src/app/lego-utils';
import { first } from 'lodash-es';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { filter, shareReplay, switchMap } from 'rxjs/operators';
import { PartDesignItemStructure } from 'src/app/models/domain';
import { RevisionMCodesService } from 'src/app/state';

@Component({
  selector: 'em-part-design-revision-structure',
  styleUrls: ['./part-design-revision-structure.component.less'],
  templateUrl: './part-design-revision-structure.component.html',
  standalone: false,
})
export class PartDesignRevisionStructureComponent implements OnInit, OnDestroy {
  @Input() public error = '';

  public readonly trackByRef = trackByRef;
  public mCodes$: Observable<string[] | undefined>;
  public isMCodesLoading$: Observable<boolean>;
  public mCodesError$: Observable<string | undefined>;

  private readonly subscriptions = new Subscription();
  private readonly selectedRevision$ = new ReplaySubject<string>();
  private structure?: PartDesignItemStructure;

  public constructor(private readonly revisionMCodesService: RevisionMCodesService) {
    this.mCodes$ = this.selectedRevision$.pipe(
      filterUndefined(),
      switchMap((revisionId) => this.revisionMCodesService.selectMCodes(revisionId)),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );

    this.isMCodesLoading$ = this.selectedRevision$.pipe(
      filterUndefined(),
      switchMap((revisionId) => this.revisionMCodesService.selectLoading(revisionId)),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );

    this.mCodesError$ = this.selectedRevision$.pipe(
      filterUndefined(),
      switchMap((revisionId) => this.revisionMCodesService.selectError(revisionId)),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );
  }

  @Input() public set partDesignStructure(
    partDesignStructure: PartDesignItemStructure | undefined
  ) {
    // We are looping through revId for each revision to find duplicates - to ensure only one of each file is shown.
    this.structure = partDesignStructure && {
      ...partDesignStructure,
      revisions: partDesignStructure.revisions.map((revision) => ({
        ...revision,
        references: revision.references.filter(
          (reference, index, array) =>
            array.findIndex((i) => i.itemUid === reference.itemUid) === index
        ),
      })),
    };

    this.revisionMCodesService.reset();

    if (!this.structure) {
      return;
    }

    const firstRevision = first(this.structure.revisions);

    if (!firstRevision) {
      return;
    }

    this.selectedRevision$.next(firstRevision.revId);
  }

  public get partDesignStructure() {
    return this.structure;
  }

  public ngOnInit(): void {
    this.subscriptions.add(
      this.selectedRevision$
        .pipe(
          filter(() => !!this.partDesignStructure),
          switchMap((revisionId) =>
            this.revisionMCodesService.load(
              (this.partDesignStructure as PartDesignItemStructure).itemId,
              revisionId
            )
          )
        )
        .subscribe()
    );
  }

  public revisionSelectionChange(index: number) {
    if (!this.partDesignStructure) {
      return;
    }

    const revisionId = this.partDesignStructure.revisions[index].revId;

    this.selectedRevision$.next(revisionId);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
